// components/Navbar.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import { AppContextisChecked } from '../context/AppContextisChecked';
import BasicModal from '../components/BasicModal.js';
import Avatar from '@mui/material/Avatar';
import { auth, database } from '../firebase/firebaseConfig';
import SubscriptionModal from './SubscriptionModal';
import { Upgrade } from '@mui/icons-material';

// SVG Icon Components
const ManageIcon = ({ isMobile }) => (
    <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v6m0 6v6m4.22-13.22l-2.83 2.83m-2.78 2.78l-2.83 2.83M20.88 13H15m-6 0H3m16.24 5.24l-2.83-2.83m-2.82-2.82l-2.83-2.83" />
    </svg>
);

const LogoutIcon = ({ isMobile }) => (
    <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const UserIcon = ({ isMobile }) => (
    <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

// Request Status Component
const RequestStatus = ({ userRequestData }) => {
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        if (userRequestData && userRequestData.freeRequestsRemaining > 0 && !userRequestData.hasSubscription) {
            setShowNotification(true);
            // Auto-hide after 10 seconds
            const timer = setTimeout(() => setShowNotification(false), 10000);
            return () => clearTimeout(timer);
        }
    }, [userRequestData]);

    if (!userRequestData || !showNotification) return null;

    const { freeRequestsRemaining, numberOfRequests, hasSubscription } = userRequestData;

    if (hasSubscription) return null;

    return (
        <></>
    );
};

const Navbar = ({ isMobile }) => {
    const [openModal, setOpenModal] = useState(false);
    const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
    const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
    const [authRequired, setAuthRequired] = useState(true);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [checkCounter, setCheckCounter] = useState(0);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [userSubscriptionData, setUserSubscriptionData] = useState(null);

    const { user, logOut, sessionTerminated, loading, userRequestData } = UserAuth();
    const { auth, setAuth } = useContext(AppContextisChecked);
    const { unpaid, setUnpaid } = useContext(AppContextisChecked);
    // console.log('Unpaid state:', unpaid);
    const navigate = useNavigate();

    // Icon button styles for mobile
    const iconButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '6px' : '8px 16px',
        cursor: 'pointer',
        background: 'transparent',
        border: 'none',
        color: 'rgb(75, 85, 99)',
        transition: 'color 0.2s, background-color 0.2s',
        borderRadius: '4px',
        marginLeft: '8px',
    };

    // Direct Firebase subscription check
    const checkFirebaseSubscription = async (userId) => {
        if (!userId) return false;

        try {
            const userRef = database.ref(`users/${userId}`);
            const snapshot = await userRef.once('value');
            const userData = snapshot.val();

            // console.log('Direct Firebase check - User data:', userData);

            if (userData && userData.subscription) {
                const subscription = userData.subscription;

                // Store subscription data for the modal
                setUserSubscriptionData(subscription);

                const isActive =
                    subscription.status === 'active' &&
                    subscription.planEndDate &&
                    new Date(subscription.planEndDate) > new Date() &&
                    subscription.paymentConfirmedAt &&
                    (subscription.subscriptionId || subscription.paymentId) &&
                    subscription.planType;

                console.log('Direct Firebase subscription validation:', {
                    status: subscription.status,
                    planEndDate: subscription.planEndDate,
                    isExpired: new Date(subscription.planEndDate) <= new Date(),
                    paymentConfirmed: !!subscription.paymentConfirmedAt,
                    hasId: !!(subscription.subscriptionId || subscription.paymentId),
                    planType: subscription.planType,
                    isActive: isActive
                });

                return isActive;
            }

            return false;
        } catch (error) {
            console.error('Error checking Firebase subscription:', error);
            return false;
        }
    };

    // Check for active subscription
    useEffect(() => {
        const checkSubscriptionStatus = async () => {
            if (!user || !user.uid) {
                setHasActiveSubscription(false);
                setUserSubscriptionData(null);
                // console.log('No user, setting hasActiveSubscription to false');
                return;
            }

            try {
                // ALWAYS do a direct Firebase check first
                const firebaseHasSubscription = await checkFirebaseSubscription(user.uid);

                if (firebaseHasSubscription) {
                    setHasActiveSubscription(true);
                    // console.log('✅ Active subscription found in Firebase');
                    return;
                }

                // If not found in Firebase, check userRequestData
                if (userRequestData && userRequestData.subscription) {
                    setUserSubscriptionData(userRequestData.subscription);

                    const hasValidSubscription =
                        userRequestData.subscription.status === 'active' &&
                        userRequestData.subscription.planEndDate &&
                        new Date(userRequestData.subscription.planEndDate) > new Date() &&
                        userRequestData.subscription.paymentConfirmedAt;

                    setHasActiveSubscription(hasValidSubscription);

                    console.log('UserRequestData subscription check:', {
                        hasSubscription: !!userRequestData.subscription,
                        status: userRequestData.subscription?.status,
                        planEndDate: userRequestData.subscription?.planEndDate,
                        paymentConfirmed: !!userRequestData.subscription?.paymentConfirmedAt,
                        isValid: hasValidSubscription
                    });
                } else {
                    // Final fallback: Check via API
                    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                    try {
                        const response = await fetch(`${API_BASE_URL}/api/v1/subscription-status/${user.uid}`);
                        if (response.ok) {
                            const data = await response.json();

                            if (data.subscription) {
                                setUserSubscriptionData(data.subscription);
                            }

                            const hasValidSubscription =
                                data.subscription &&
                                data.subscription.status === 'active' &&
                                data.subscription.planEndDate &&
                                new Date(data.subscription.planEndDate) > new Date() &&
                                data.subscription.paymentConfirmedAt;

                            setHasActiveSubscription(hasValidSubscription);

                            console.log('API subscription check:', {
                                hasSubscription: !!data.subscription,
                                status: data.subscription?.status,
                                planEndDate: data.subscription?.planEndDate,
                                paymentConfirmed: !!data.subscription?.paymentConfirmedAt,
                                isValid: hasValidSubscription
                            });
                        } else {
                            setHasActiveSubscription(false);
                        }
                    } catch (apiError) {
                        console.warn('API check failed:', apiError);
                        setHasActiveSubscription(false);
                    }
                }
            } catch (error) {
                console.error('Error checking subscription status:', error);
                setHasActiveSubscription(false);
            }
        };

        checkSubscriptionStatus();
    }, [user, userRequestData, checkCounter]);

    // Listen for URL changes (when returning from Stripe)
    useEffect(() => {
        // Check if we're returning from a successful payment
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (sessionId && user?.uid) {
            // console.log('Detected return from Stripe with session:', sessionId);
            // Force a re-check after a short delay to allow Firebase to update
            setTimeout(() => {
                setCheckCounter(prev => prev + 1);
            }, 2000);
        }
    }, [window.location.search, user]);

    // Also set up a Firebase realtime listener for subscription changes
    useEffect(() => {
        if (!user?.uid) return;

        const userRef = database.ref(`users/${user.uid}/subscription`);
        const listener = userRef.on('value', (snapshot) => {
            const subscription = snapshot.val();
            // console.log('Firebase subscription realtime update:', subscription);

            if (subscription) {
                setUserSubscriptionData(subscription);

                const isActive =
                    subscription.status === 'active' &&
                    subscription.planEndDate &&
                    new Date(subscription.planEndDate) > new Date() &&
                    subscription.paymentConfirmedAt;

                setHasActiveSubscription(isActive);
                // console.log('Realtime subscription status:', isActive);
            }
        });

        return () => {
            userRef.off('value', listener);
        };
    }, [user]);

    // Add a global refresh function
    useEffect(() => {
        window.refreshSubscriptionStatus = () => {
            // console.log('Manual subscription refresh triggered');
            setCheckCounter(prev => prev + 1);
        };

        return () => {
            delete window.refreshSubscriptionStatus;
        };
    }, []);

    const handleSignOut = async () => {
        try {
            await logOut();
            setAuth(false);
            setHasActiveSubscription(false);
            setUserSubscriptionData(null);
            // After logout, require authentication again
            setOpenModal(true);
            setAuthRequired(true);
        } catch (error) {
            // console.log(error);
        }
    };

    // Auto-open modal when user is not authenticated
    useEffect(() => {
        // Wait for auth to finish loading before checking
        if (!loading && !hasCheckedAuth) {
            setHasCheckedAuth(true);

            if (!user) {
                setOpenModal(true);
                setAuthRequired(true);
            } else {
                setAuthRequired(false);
                // Make sure modal is closed if user is already authenticated
                setOpenModal(false);
            }
        }
    }, [user, loading, hasCheckedAuth]);

    // Monitor user state changes
    useEffect(() => {
        if (user && user.uid) {
            // User is authenticated, allow modal to close
            setAuthRequired(false);
        } else {
            // User is not authenticated, require auth
            setAuthRequired(true);
        }
    }, [user]);

    // Handle session termination
    useEffect(() => {
        if (sessionTerminated) {
            setAuth(false);
            setOpenModal(true);
            setAuthRequired(true);
            setHasActiveSubscription(false);
            setUserSubscriptionData(null);
        }
    }, [sessionTerminated, setAuth]);

    const handleOpen = () => {
        // Don't open auth modal if user is already authenticated
        if (!user) {
            setOpenModal(true);
        }
    }

    const handleOpenSubscription = () => {
        setOpenSubscriptionModal(true);
    }

    // Mobile view - render icon buttons
    if (isMobile) {
        return (
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '4px' }}>
                <BasicModal
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    requireAuth={authRequired}
                />
                <BasicModal
                    openModal={openSubscriptionModal}
                    setOpenModal={setOpenSubscriptionModal}
                    initialView="subscription"
                    requireAuth={false}
                />
                <SubscriptionModal
                    isOpen={showSubscriptionModal}
                    onClose={() => setShowSubscriptionModal(false)}
                    userSubscription={userSubscriptionData}
                />

                {user ? (
                    // Logged in mobile view
                    <>
                        <button
                            style={iconButtonStyle}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => console.log('User profile clicked')}
                            title="Profile"
                        >
                            <UserIcon isMobile={isMobile} />
                        </button>

                        {/* Show Manage icon if has subscription, otherwise show Upgrade icon */}
                        {/* {hasActiveSubscription ? (
                            <button 
                                style={iconButtonStyle}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={() => setShowSubscriptionModal(true)}
                                title="Manage"
                            >
                                <ManageIcon isMobile={isMobile} />
                            </button>
                        ) : (
                            <button 
                                style={iconButtonStyle}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={handleOpenSubscription}
                                title="Upgrade"
                            >
                                <ManageIcon isMobile={isMobile} />
                            </button>
                        )} */}
                        {/* CONDITIONALLY RENDER UPGRADE BUTTON - Only show if NO active subscription */}
                        {!hasActiveSubscription && user && (
                            <button
                                onClick={handleOpenSubscription}
                                style={{
                                    background: unpaid
                                        ? '#ef4444'
                                        : '#202123',
                                    color: 'white',
                                    fontWeight: '500',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '5px 14px',
                                    position: 'relative',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    minWidth: '80px',
                                    // animation: unpaid
                                    //     ? 'pulse 2s infinite'
                                    //     : 'none'
                                }}
                                onMouseOver={(e) => {
                                    if (unpaid) {
                                        e.currentTarget.style.background = '#dc2626';
                                    } else {
                                        e.currentTarget.style.background = '#2a2b2d';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (unpaid) {
                                        e.currentTarget.style.background = '#ef4444';
                                    } else {
                                        e.currentTarget.style.background = '#202123';
                                    }
                                }}
                            >
                                {/* {userRequestData && userRequestData.freeRequestsRemaining === 0 && !userRequestData.hasSubscription
                                    ? 'Upgrade Now'
                                    : 'Upgrade'} */}
                                {unpaid?'Upgrade Now':'Upgrade'}
                            </button>
                        )}

                        {/* SUBSCRIPTION MANAGEMENT BUTTON - Only show if user HAS active subscription */}
                        {hasActiveSubscription && (
                            <button
                                onClick={() => setShowSubscriptionModal(true)}
                                style={{
                                    background: '#202123',
                                    color: 'white',
                                    fontWeight: '500',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '5px 14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    minWidth: '80px'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = '#2a2b2d'
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = '#202123'
                                }}
                            >
                                Manage
                            </button>
                        )}
                        <button
                            style={iconButtonStyle}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={handleSignOut}
                            title="Log out"
                        >
                            <LogoutIcon isMobile={isMobile} />
                        </button>
                    </>
                ) : (
                    // Not logged in mobile view - show login button
                    <button
                        style={iconButtonStyle}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={handleOpen}
                        title="Log in"
                    >
                        <UserIcon isMobile={isMobile} />
                    </button>
                )}
            </div>
        );
    }

    // Desktop view - original Navbar content
    return (
        <div style={{ width: '100%' }}>
            <RequestStatus userRequestData={userRequestData} />

            {/* Add Subscription Management Modal */}
            <SubscriptionModal
                isOpen={showSubscriptionModal}
                onClose={() => setShowSubscriptionModal(false)}
                userSubscription={userSubscriptionData}
            />

            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                width: '100%',
                padding: '12px 16px',
                background: 'transparent',
                color: 'white',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                zIndex: 1000
            }}>
                <BasicModal
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    requireAuth={authRequired}
                />
                <BasicModal
                    openModal={openSubscriptionModal}
                    setOpenModal={setOpenSubscriptionModal}
                    initialView="subscription"
                    requireAuth={false}
                />
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    {user?.displayName && (
                        <span style={{
                            fontSize: '14px',
                            color: '#e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginRight: '8px'
                        }}>
                            <Avatar style={{ width: '32px', height: '32px' }} src="/broken-image.jpg" />
                            {user.displayName}
                            {/* Show PRO badge if user has active subscription */}
                            {hasActiveSubscription && (
                                <span style={{
                                    backgroundColor: '#6366f1',
                                    color: 'white',
                                    fontSize: '11px',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    PRO
                                </span>
                            )}
                        </span>
                    )}

                    {/* CONDITIONALLY RENDER UPGRADE BUTTON - Only show if NO active subscription */}
                    {!hasActiveSubscription && user && (
                        <button
                            onClick={handleOpenSubscription}
                            style={{
                                background: unpaid
                                    ? '#ef4444'
                                    : '#202123',
                                color: 'white',
                                fontWeight: '500',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 16px',
                                position: 'relative',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                minWidth: '80px',
                                animation: unpaid
                                    ? 'pulse 2s infinite'
                                    : 'none'
                            }}
                            onMouseOver={(e) => {
                                if (unpaid) {
                                    e.currentTarget.style.background = '#dc2626';
                                } else {
                                    e.currentTarget.style.background = '#2a2b2d';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (unpaid) {
                                    e.currentTarget.style.background = '#ef4444';
                                } else {
                                    e.currentTarget.style.background = '#202123';
                                }
                            }}
                        >
                            {unpaid
                                ? 'Upgrade Now'
                                : 'Upgrade'}
                        </button>
                    )}

                    {/* SUBSCRIPTION MANAGEMENT BUTTON - Only show if user HAS active subscription */}
                    {hasActiveSubscription && (
                        <button
                            onClick={() => setShowSubscriptionModal(true)}
                            style={{
                                background: '#202123',
                                color: 'white',
                                fontWeight: '500',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 16px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                minWidth: '80px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#2a2b2d'
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#202123'
                            }}
                        >
                            Manage
                        </button>
                    )}

                    {user?.displayName ? (
                        <button
                            onClick={handleSignOut}
                            style={{
                                background: '#202123',
                                color: 'white',
                                fontWeight: '500',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 16px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                minWidth: '80px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#2a2b2d'
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#202123'
                            }}
                        >
                            Log out
                        </button>
                    ) : (
                        <button
                            onClick={handleOpen}
                            style={{
                                background: '#202123',
                                color: 'white',
                                fontWeight: '500',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 16px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                minWidth: '80px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#2a2b2d'
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#202123'
                            }}
                        >
                            Log in
                        </button>
                    )}
                </div>
            </div>

            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    
                    @keyframes pulse {
                        0% {
                            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
                        }
                        70% {
                            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
                        }
                        100% {
                            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default Navbar;
